'use client';

import { createContext, useContext, useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export default function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>('dark');
  const [mounted, setMounted] = useState(false);
  const [hasExplicitPreference, setHasExplicitPreference] = useState(false);

  useEffect(() => {
    setMounted(true);
    const root = window.document.documentElement;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const savedTheme = localStorage.getItem('theme');

    const applyTheme = (nextTheme: Theme) => {
      root.classList.remove('light', 'dark');
      root.classList.add(nextTheme);
      root.dataset.theme = nextTheme;
      setTheme(nextTheme);
    };

    if (savedTheme === 'light' || savedTheme === 'dark') {
      setHasExplicitPreference(true);
      applyTheme(savedTheme);
      return;
    }

    setHasExplicitPreference(false);
    applyTheme(mediaQuery.matches ? 'dark' : 'light');

    const handleMediaChange = (event: MediaQueryListEvent) => {
      if (localStorage.getItem('theme')) return;
      applyTheme(event.matches ? 'dark' : 'light');
    };

    mediaQuery.addEventListener('change', handleMediaChange);
    return () => mediaQuery.removeEventListener('change', handleMediaChange);
  }, []);

  useEffect(() => {
    if (mounted) {
      const root = window.document.documentElement;
      root.classList.remove('light', 'dark');
      root.classList.add(theme);
      root.dataset.theme = theme;
      if (hasExplicitPreference) {
        localStorage.setItem('theme', theme);
      } else {
        localStorage.removeItem('theme');
      }
    }
  }, [theme, mounted, hasExplicitPreference]);

  const toggleTheme = () => {
    setHasExplicitPreference(true);
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light');
  };

  // Render the same subtree on the server and first client paint.
  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
